import {
  AuditEventDataConnectionFragment,
  AuditEventDataFragment,
  GetAuditEventListQueryVariables,
  GetAuditEventQueryVariables,
  Omit,
  useGetAuditEventListQuery,
  useGetAuditEventQuery
} from "generated/graphql";
import { useMemo } from "react";
import { parseIntSecondsTimeMilliseconds } from "utilities/datetime";
import * as Urql from "polygraphql";
import * as t from "generated/graphql.schema";
import {
  AuditEventType,
  getAuditEventType,
  getEntitlementName,
  toAuditEventType
} from "utilities/licenseServerUtils";

export type AuditEvent = {
  recordId: string;
  tenantId: string;
  cursor: any;
  eventTypeEnum: AuditEventType;
  eventTypeName: string | undefined;
  eventTime: number;
  userId: string;
  userName?: string;
  entitlementName: string | undefined;
  productName: string;
  previousValue: string;
  newValue: string;
  additionalInfo: string;
};

export type AuditEventEdge = {
  cursor: string;
  node: AuditEvent;
};

export type AuditEventConnection = {
  edges: Array<AuditEventEdge>;
  pageInfo: Pick<t.PageInfo, "hasNextPage" | "endCursor">;
  totalCount: number;
};

export const toAuditEvent = (
  auditEventData: AuditEventDataFragment,
  cursor: string
): AuditEvent => {
  const eventTypeEnum = toAuditEventType(auditEventData.eventType);
  return {
    eventTime: parseIntSecondsTimeMilliseconds(auditEventData.eventTime),
    cursor: cursor,
    recordId: auditEventData.recordId,
    tenantId: auditEventData.tenantId,
    // TODO: Wire up user ID once the server supports both user ID and name. Currently, the name is the ID.
    userId: auditEventData.userName,
    // TODO: Wire up user name once the server supports both user ID and name.
    userName: undefined,
    eventTypeEnum: eventTypeEnum,
    eventTypeName: getAuditEventType(eventTypeEnum),
    entitlementName: getEntitlementName(auditEventData.entitlementName),
    productName: auditEventData.instanceName,
    previousValue: auditEventData.previousValue,
    newValue: auditEventData.newValue,
    additionalInfo: auditEventData.additionalInfo
  };
};

export const toAuditEventConnection = (
  connection: AuditEventDataConnectionFragment
): AuditEventConnection => {
  const edges: Array<AuditEventEdge> = connection.edges.map(edge => {
    return {
      node: toAuditEvent(edge.node, edge.cursor),
      cursor: edge.cursor
    };
  });

  return {
    totalCount: connection.totalCount,
    pageInfo: connection.pageInfo,
    edges: edges
  };
};

export type GetAuditEventListOptions = Omit<
  Urql.UseQueryArgs<GetAuditEventListQueryVariables>,
  "query"
>;

export const useGetAuditEventList = (
  options: GetAuditEventListOptions = {}
) => {
  const pause = !options.variables?.tenantId && (options?.pause ?? false);
  const queryResults = useGetAuditEventListQuery({
    ...options,
    pause
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.searchAuditEvents;
    if (sourceData) {
      result = toAuditEventConnection(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};

export type GetAuditEventOptions = Omit<
  Urql.UseQueryArgs<GetAuditEventQueryVariables & { tenantId: string }>,
  "query"
>;

export const useGetAuditEvent = (options: GetAuditEventOptions = {}) => {
  const pause = !options.variables?.tenantId && (options?.pause ?? false);
  const queryResults = useGetAuditEventQuery({
    ...options,
    pause
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getAuditEvent;
    if (sourceData) {
      result = toAuditEvent(sourceData, "");
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};
